import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Popover,
  Pressable,
  Text,
  View,
} from 'native-base';
import {
  Boxes,
  Truck,
  ArchiveRestore,
  ShieldCheck,
  ChevronDown,
} from 'lucide-react-native';
import { DeanteData } from '../../Universal/API/Context-API/AppProvider';
import { Platform, StyleSheet } from 'react-native';
import { webFont } from '../../Universal/API/API';
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedStyle,
  interpolate,
} from 'react-native-reanimated';
import { useAppContext } from '../../Universal/API/Context-API/AppContext';
import Poland from '../../../assets/Icons/Flags/Poland';
import Russia from '../../../assets/Icons/Flags/Russia';
import { isString } from 'lodash';
import { toNum } from '../../Universal/API/Context-API/utils';


interface Props {
  scaleAll: number;
  compact?: boolean;
  unit: DeanteData;
}

const OstatkiDisplay: React.FC<Props> = ({ unit, scaleAll, compact = false }) => {
  const { isTooSmall } = useAppContext();

  const ostatki = unit.ostatki;
  const ostatkiPoland = unit.ostatkiPoland;

  // --------- DATA / ENTRIES (NO RETURNS HERE) --------- //

  const allEntries = useMemo(
    () => {
      if (!ostatki && !ostatkiPoland) {
        return [] as {
          key: string;
          label: string;
          value: number;
          icon: React.ComponentType<any>;
          replace?: React.ReactNode;
        }[];
      }

// poland
      const poland_stock =  toNum(ostatkiPoland?.stock ?? 0)
// spb
      const spb_stock = toNum(ostatki?.FreeSklad ?? 0)
      const spb_v_puti = toNum(ostatki?.VPutiFree?? 0)
      const spb_reserv = toNum(ostatki?.ReservSklad ?? 0)
      const spb_v_puti_reserv = toNum(ostatki?.VPutiReserv ?? 0)
// msk
      const msk_v_puti =  toNum(ostatki?.VPutiFreeMSK?? 0)
      const msk_stock = toNum(ostatki?.FreeSkladMSK ?? 0)
      const msk_reserv = toNum(ostatki?.ReservSkladMSK ?? 0)
      const msk_v_puti_reserv = toNum(ostatki?.VPutiReservMSK ?? 0)
// summary
      const all = spb_stock +msk_stock + poland_stock

      
      return [
        {
          key: 'FreeSkladAll',
          label: 'Свободно всего',
          value: poland_stock >= 100 ? all + '+' : all,
          icon: Boxes,
        },
        {
          key: 'FreeSklad',
          label: 'Свободно в СПБ',
          value: spb_stock,
          icon: Russia,
        },
        {
          key: 'FreeSkladMSK',
          label: 'Свободно в МСК',
          value: msk_stock,
          icon: Russia,
        },
        {
          key: 'FreeSkladPoland',
          label: 'Свободно в Польше',
          value: poland_stock >= 100 ?poland_stock+ '+' : poland_stock,
          icon: Poland,
        },
        {
          key: 'VPutiFree',
          label: 'В пути (свободно) СПБ',
          value: spb_v_puti,
          icon: Truck,
        },
        {
          key: 'VPutiFreeMSK',
          label: 'В пути (свободно) МСК',
          value: msk_v_puti,
          icon: Truck,
        },
        {
          key: 'ReservSklad',
          label: 'Резерв СПБ',
          value: spb_reserv,
          icon: ArchiveRestore,
        },
        {
          key: 'ReservSkladMSK',
          label: 'Резерв МСК',
          value: msk_reserv,
          icon: ArchiveRestore,
        },
        {
          key: 'VPutiReserv',
          label: 'В пути (резерв) СПБ',
          value: spb_v_puti_reserv,
          icon: ShieldCheck,
        },
        {
          key: 'VPutiReservMSK',
          label: 'В пути (резерв) МСК',
          value: msk_v_puti_reserv,
          icon: ShieldCheck,
        },
      ];
    },
    [ostatki, ostatkiPoland],
  );

  const entries = allEntries.filter(e => e.value > 0 || (isString(e.value) && e.value.includes('+')));
  const hasEntries = entries.length > 0;

  const ED = () => (
    <Text color="black" fontWeight="medium">
      {' '}
      {unit.ed}
    </Text>
  );

  // --------- HOOKS (ALWAYS CALLED, NO CONDITIONS) --------- //

  // full mode
  const [expanded, setExpanded] = useState(false);
  const animatedHeight = useSharedValue(0);
  const rotate = useSharedValue(0);

  // compact mode
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // styles
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          borderRadius: 12 * scaleAll,
          padding: 12 * scaleAll,
          backgroundColor: '#fff',
          overflow: 'hidden',
        },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8 * scaleAll,
          height: 34 * scaleAll,
        },
        rowCompact: {
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderRadius: 10 * scaleAll,
          borderWidth: 0.5,
          borderStyle: 'dashed',
          paddingHorizontal: 8 * scaleAll,
          paddingVertical: 4 * scaleAll,
          gap: 6 * scaleAll,
        },
        label: {
          flex: 1,
          fontSize: 14 * scaleAll,
          fontFamily: webFont('Inter'),
          color: '#333',
          fontWeight: '500',
        },
        labelCompact: {
          fontSize: 12 * scaleAll,
          fontFamily: webFont('Inter'),
          color: '#0A84FF',
          fontWeight: '600',
        },
        labelPopover: {
          flex: 1,
          fontSize: 13 * scaleAll,
          fontFamily: webFont('Inter'),
          color: '#333',
          fontWeight: '500',
        },
        value: {
          fontSize: 13 * scaleAll,
          fontFamily: webFont('Inter'),
          fontWeight: '700',
          color: '#0A84FF',
        },
        popoverRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8 * scaleAll,
          paddingVertical: 4 * scaleAll,
        },
        arrow: {
          width: 24 * scaleAll,
          height: 24 * scaleAll,
          justifyContent: 'center',
          alignItems: 'center',
        },
        expandable: {
          overflow: 'hidden',
        },
        shadow: {
          shadowColor: 'black',
          shadowOffset: { width: 1, height: 1 },
          shadowOpacity: 0.15,
          shadowRadius: 4,
          elevation: 3,
        },
      }),
    [scaleAll],
  );

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    animatedHeight.value = withTiming(next ? 1 : 0, { duration: 300 });
    rotate.value = withTiming(next ? 1 : 0, { duration: 300 });
  };

  useEffect(() => {
    // auto expand on mount
    toggleExpand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || !compact) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        !target.closest('.popover-trigger') &&
        !target.closest('.native-base__popover-content')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [compact]);

  const heightStyle = useAnimatedStyle(() => ({
    height: interpolate(
      animatedHeight.value,
      [0, 1],
      [0, Math.max(0, (entries.length - 1) * 34 * scaleAll)],
    ),
    opacity: animatedHeight.value,
  }));

  const rotateStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${interpolate(rotate.value, [0, 1], [0, 180])}deg` }],
  }));

  const Icon = entries[0]?.icon;
  const showToggle = entries.length > 1;

  // --------- SAFE RETURN NULL (AFTER HOOKS) --------- //

  if (!hasEntries || !Icon) {
    return null;
  }

  // --------- COMPACT RENDER --------- //

  if (compact) {
    const handleHoverIn = () => {
      setIsHovered(true);
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
      setIsOpen(true);
    };

    const handleHoverOut = () => {
      setIsHovered(false);
      closeTimeout.current = setTimeout(() => {
        setIsOpen(false);
      }, 400);
    };

    return (
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={triggerProps => (
          <Pressable
            className="popover-trigger"
            {...triggerProps}
            onPress={() => setIsOpen(prev => !prev)}
            onHoverIn={handleHoverIn}
            onHoverOut={handleHoverOut}
          >
            <View style={[styles.rowCompact, isHovered && styles.shadow]}>
              {!isTooSmall && (
                <Icon color="#2e2e2e" size={16 * scaleAll} strokeWidth={2} />
              )}
              <Text style={styles.labelCompact}>
                {entries[0].value}
                <ED />
              </Text>
              {!isTooSmall && entries.length > 1 && (
                <ChevronDown color="#0A84FF" size={14 * scaleAll} />
              )}
            </View>
          </Pressable>
        )}
        placement="bottom right"
      >
        <Popover.Content
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          borderRadius="md"
          backgroundColor="white"
          p="2"
        >
          {entries.map(entry => {
            const EntryIcon = entry.icon;
            // @ts-ignore optional replace
            const replace = entry.replace;
            return (
              <View key={entry.key} style={styles.popoverRow}>
                {replace ?? (
                  <EntryIcon
                    color="#2e2e2e"
                    size={16 * scaleAll}
                    strokeWidth={2}
                  />
                )}
                <Text style={styles.labelPopover}>{entry.label}</Text>
                <Text style={styles.value}>
                  {entry.value}
                  <ED />
                </Text>
              </View>
            );
          })}
        </Popover.Content>
      </Popover>
    );
  }

  // --------- FULL RENDER --------- //

  return (
    <Box style={styles.container} shadow={4}>
      <Pressable
        className="popover-trigger"
        onPress={toggleExpand}
        disabled={!showToggle}
      >
        <View style={styles.row}>
          <Icon color="#2e2e2e" size={20 * scaleAll} strokeWidth={2} />
          <Text style={styles.label}>{entries[0].label}</Text>
          <Text style={styles.value}>
            {entries[0].value}
            <ED />
          </Text>
          {showToggle && (
            <Animated.View style={[styles.arrow, rotateStyle]}>
              <ChevronDown color="#0A84FF" size={18 * scaleAll} />
            </Animated.View>
          )}
        </View>
      </Pressable>

      {showToggle && (
        <Animated.View style={[styles.expandable, heightStyle]}>
          {entries.slice(1).map(entry => {
            const EntryIcon = entry.icon;
            // @ts-ignore optional replace
            const replace = entry.replace;
            return (
              <View key={entry.key} style={styles.row}>
                {replace ?? (
                  <EntryIcon
                    color="#2e2e2e"
                    size={20 * scaleAll}
                    strokeWidth={2}
                  />
                )}
                <Text style={styles.label}>{entry.label}</Text>
                <Text style={styles.value}>
                  {entry.value}
                  <ED />
                </Text>
              </View>
            );
          })}
        </Animated.View>
      )}
    </Box>
  );
};

export default React.memo(OstatkiDisplay);
